
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trophy, Calendar, Award, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";

interface Organizer {
  id: string;
  name: string;
  rating: number;
  eventsPerYear: number;
  avatar?: string;
  upcomingEvents: number;
  sports: string[];
}

interface FeaturedOrganizersSectionProps {
  organizers: Organizer[];
  onOrganizerClick: (organizer: Organizer) => void;
  selectedSport: string;
  onExploreMore?: () => void;
}

const FeaturedOrganizersSection = ({ organizers, onOrganizerClick, selectedSport, onExploreMore }: FeaturedOrganizersSectionProps) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();

  const getInitials = (name: string) => {
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  const filteredOrganizers = organizers;

  return (
    <Card className="bg-transparent border-none shadow-none pb-6 px-0 md:px-6">
      <CardHeader className="py-6 px-4 md:px-6">
        <div className="flex items-center justify-between gap-4 md:gap-8">
          <CardTitle className="text-sm md:text-xl font-bold text-gray-900 flex items-center">
            <Award className="h-5 w-5 mr-2 text-[#3D6B4F]" />
            {t('dashboard.featuredOrganizers')}
          </CardTitle>
          {onExploreMore && (
            <Button variant="outline" size="sm" onClick={onExploreMore} className="flex items-center gap-1 text-xs md:text-sm h-6 md:h-10 px-2 md:px-4">
              {t('common.viewAll')}
              <ArrowRight className="h-3 w-3 md:h-4 md:w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 md:px-6">
        <div className="space-y-4">
          {filteredOrganizers.map((organizer) => (
            <Card 
              key={organizer.id} 
              className="hover:shadow-lg transition-shadow duration-200 bg-white border-gray-200 cursor-pointer"
              onClick={() => onOrganizerClick(organizer)}
            >
              <CardContent className={`${isMobile ? 'p-3' : 'p-6'}`}>
                <div className={`flex ${isMobile ? 'items-center gap-2' : 'items-start gap-4'}`}>
                  {/* Avatar */}
                  <div className={`${isMobile ? 'w-10 h-10' : 'w-16 h-16'} bg-primary rounded-full flex items-center justify-center text-primary-foreground ${isMobile ? 'text-sm' : 'text-lg'} font-bold shrink-0 overflow-hidden`}>
                    {organizer.avatar ? (
                      <img src={organizer.avatar} alt={organizer.name} className="w-full h-full object-cover" />
                    ) : (
                      getInitials(organizer.name)
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className={`${isMobile ? 'text-sm' : 'text-lg'} font-bold text-gray-900 truncate`}>
                      {organizer.name}
                    </h3>
                    <div className={`flex items-center ${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>
                      <Trophy className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-1 shrink-0`} />
                      <span>{organizer.eventsPerYear} {t('explore.eventsHeld')}</span>
                    </div>
                    <div className={`flex items-center ${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>
                      <Calendar className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-1 shrink-0`} />
                      <span>{organizer.upcomingEvents} {t('explore.upcomingEvents')}</span>
                    </div>
                  </div>
                  
                  {/* Button */}
                  <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button 
                      size={isMobile ? "xs" : "sm"}
                      className="bg-primary hover:bg-primary/90"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOrganizerClick(organizer);
                      }}
                    >
                      {t('explore.viewProfile')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default FeaturedOrganizersSection;
